export type Service = {
  slug: string;
  name: string;
  shortName: string;
  kicker: string;
  summary: string;
  answer: string;
  price: string;
  image: string;
  alt: string;
  features: string[];
};

export type GalleryItem = {
  id: number;
  image: string;
  category: string;
  room: string;
  caption: string;
  alt: string;
  featured?: boolean;
};

export const services: Service[] = [
  {
    slug: "carpets",
    name: "Carpet supply & fitting",
    shortName: "Carpets",
    kicker: "Quiet, warm and properly finished",
    summary: "From hard-wearing family rooms to beautifully bound stair runners, we help you choose a carpet that suits the way you live.",
    answer: "A good carpet starts with the right underlay and an accurate measure. We bring both together, then fit neatly around every edge, stair and doorway.",
    price: "From £18/m² inc. VAT",
    image: "/images/gallery/fb_image_6_20260730.jpg",
    alt: "Cream stair runner with dark edge binding fitted by Horncastle Flooring",
    features: ["Free home measure", "Underlay advice", "Stairs and runners", "Furniture-friendly planning"],
  },
  {
    slug: "lvt-luxury-vinyl-tile",
    name: "Luxury vinyl tile",
    shortName: "LVT",
    kicker: "A practical floor with a natural look",
    summary: "Tough, water-resistant and easy to live with. LVT is ideal for kitchens, hallways and busy family spaces.",
    answer: "LVT gives you the grain and warmth of timber with everyday practicality. The preparation underneath matters most, so we check the subfloor before recommending a product.",
    price: "From £32/m² inc. VAT",
    image: "/images/gallery/fb_image_9_20260730.jpg",
    alt: "Warm oak-effect LVT fitted through a modern Lincolnshire kitchen",
    features: ["Subfloor assessment", "Latex preparation", "Water-resistant ranges", "Plank and tile formats"],
  },
  {
    slug: "laminate-flooring",
    name: "Laminate flooring",
    shortName: "Laminate",
    kicker: "Hard-wearing style, sensible value",
    summary: "A smart choice for living rooms, bedrooms and rentals, with modern finishes that feel far removed from old-style laminate.",
    answer: "Laminate is a durable floating floor and often offers excellent value. We will check moisture, levels and expansion gaps before recommending it for your room.",
    price: "From £24/m² inc. VAT",
    image: "/images/gallery/fb_image_4_20260730.jpg",
    alt: "Natural plank flooring fitted in a bright galley kitchen",
    features: ["Wide choice of finishes", "Neat door transitions", "Rental-friendly options", "Fast installation"],
  },
  {
    slug: "vinyl-flooring",
    name: "Vinyl flooring",
    shortName: "Vinyl",
    kicker: "Comfortable, clean and water-ready",
    summary: "Sheet vinyl is a dependable choice for bathrooms, utilities and kitchens, especially when fitted over a properly prepared base.",
    answer: "Modern sheet vinyl is cushioned, easy to clean and well suited to wet rooms. A smooth result depends on preparation, so we can include latex smoothing where needed.",
    price: "From £20/m² inc. VAT",
    image: "/images/gallery/fb_image_2_20260730.jpg",
    alt: "Dark grey vinyl fitted neatly around white kitchen units",
    features: ["Bathroom and kitchen ranges", "Latex preparation", "Low-maintenance finish", "Warm underfoot"],
  },
  {
    slug: "herringbone-parquet",
    name: "Herringbone flooring",
    shortName: "Herringbone",
    kicker: "A statement pattern, laid with precision",
    summary: "The geometry has to be right. We set out the centre line carefully so the pattern flows through the room rather than drifting off course.",
    answer: "Herringbone LVT creates a parquet look with modern durability. Careful setting-out, border planning and subfloor preparation are essential for a balanced finish.",
    price: "From £45/m² inc. VAT",
    image: "/images/gallery/fb_image_12_20260730.jpg",
    alt: "Dark herringbone flooring flowing around an oak staircase",
    features: ["Careful pattern setting-out", "Feature borders", "Subfloor preparation", "Room-to-room planning"],
  },
  {
    slug: "artificial-grass",
    name: "Artificial grass",
    shortName: "Artificial grass",
    kicker: "A tidy garden in every season",
    summary: "We help match pile height, feel and drainage to your space, then prepare and finish the base for a natural-looking lawn.",
    answer: "Artificial grass works best with the right drainage and base preparation. We measure the area, discuss how it is used and recommend a pile that suits children, pets or low-maintenance gardens.",
    price: "Measured to quote",
    image: "/images/gallery/storefront.jpg",
    alt: "Horncastle Flooring Retailer showroom with flooring ranges on display",
    features: ["Drainage assessment", "Pet-friendly options", "Secure edging", "Free site measure"],
  },
];

export const gallery: GalleryItem[] = [
  { id: 1, image: "/images/gallery/storefront.jpg", category: "Showroom", room: "Horncastle", caption: "Our Wharf Road showroom", alt: "Horncastle Flooring Retailer shop frontage at Eggbert Court in Horncastle", featured: true },
  { id: 2, image: "/images/gallery/fb_image_1_20260730.jpg", category: "Carpet", room: "Lounge", caption: "Soft grey lounge carpet", alt: "Soft grey carpet fitted around a fireplace in a Lincolnshire lounge" },
  { id: 3, image: "/images/gallery/fb_image_2_20260730.jpg", category: "Vinyl", room: "Kitchen", caption: "Clean lines in a busy kitchen", alt: "Dark grey vinyl flooring fitted around white kitchen cabinets" },
  { id: 4, image: "/images/gallery/fb_image_3_20260730.jpg", category: "Carpet", room: "Stairs", caption: "A precise staircase finish", alt: "Neutral carpet expertly fitted on a turning staircase", featured: true },
  { id: 5, image: "/images/gallery/fb_image_4_20260730.jpg", category: "Laminate", room: "Kitchen", caption: "Natural planks, neatly aligned", alt: "Natural plank flooring running through a galley kitchen" },
  { id: 6, image: "/images/gallery/fb_image_5_20260730.jpg", category: "Herringbone", room: "Hallway", caption: "Warm herringbone entrance", alt: "Warm oak herringbone floor fitted in a compact hallway", featured: true },
  { id: 7, image: "/images/gallery/fb_image_6_20260730.jpg", category: "Carpet", room: "Stairs", caption: "Bound stair runner detail", alt: "Cream stair runner with dark bound edging on white stairs", featured: true },
  { id: 8, image: "/images/gallery/fb_image_7_20260730.jpg", category: "Carpet", room: "Hallway", caption: "A calm, connected landing", alt: "Neutral fitted carpet continuing from a landing into adjoining rooms" },
  { id: 9, image: "/images/gallery/fb_image_8_20260730.jpg", category: "Carpet", room: "Stairs", caption: "Made tidy to the last edge", alt: "Taupe carpet fitted across a landing and staircase" },
  { id: 10, image: "/images/gallery/fb_image_9_20260730.jpg", category: "LVT", room: "Kitchen", caption: "Oak-look LVT for open-plan living", alt: "Oak-effect LVT fitted across a modern open-plan kitchen", featured: true },
  { id: 11, image: "/images/gallery/fb_image_10_20260730.jpg", category: "Carpet", room: "Bedroom", caption: "Soft bedroom finish", alt: "Pale plush carpet fitted wall to wall in a bedroom" },
  { id: 12, image: "/images/gallery/fb_image_11_20260730.jpg", category: "Carpet", room: "Stairs", caption: "Care around existing joinery", alt: "Carpet fitted neatly around an oak staircase and newel post" },
  { id: 13, image: "/images/gallery/fb_image_12_20260730.jpg", category: "Herringbone", room: "Landing", caption: "Herringbone around an oak stairwell", alt: "Dark herringbone flooring fitted around an oak staircase", featured: true },
];

export const areas = [
  ["horncastle", "Horncastle", "Our home town. Visit the showroom on Wharf Road or book a free home measure."],
  ["louth", "Louth", "Flooring supply and fitting for homes, rentals and new-builds across Louth."],
  ["woodhall-spa", "Woodhall Spa", "Thoughtful flooring advice and careful fitting for Woodhall Spa homes."],
  ["skegness", "Skegness", "Practical, hard-wearing floors for coastal homes, rentals and holiday properties."],
  ["lincoln", "Lincoln", "Independent flooring expertise for Lincoln homeowners and landlords."],
  ["spilsby", "Spilsby", "Carpet, LVT, vinyl and laminate supplied and fitted in and around Spilsby."],
  ["wragby", "Wragby", "Free measuring and trusted local fitting for Wragby and nearby villages."],
] as const;

export const reviews = [
  { quote: "Fair pricing, flexible service and a finish we were really pleased with.", name: "Facebook customer", source: "Recommendation" },
  { quote: "They spotted what another company had got wrong and explained it honestly.", name: "George Broughton", source: "Facebook recommendation" },
  { quote: "Knowledgeable from choosing the floor right through to fitting day.", name: "Local customer", source: "Facebook recommendation" },
  { quote: "When the roll would not fit in the car, local delivery was sorted the same day.", name: "Showroom customer", source: "Facebook recommendation" },
];

export const faqs = [
  ["Do you measure for free?", "Yes. We offer a free, no-obligation measure for homes in Horncastle and the surrounding Lincolnshire area."],
  ["Do you supply and fit?", "Yes. You can choose your flooring through us and have it fitted by experienced local fitters, all arranged in one place."],
  ["How much will my flooring cost?", "It depends on the product, room size and preparation needed. Our guide prices include VAT, and your final price is confirmed after a free measure."],
  ["Can I visit the showroom?", "Yes. Find us at Eggbert Court, Wharf Road, Horncastle, LN9 5FQ. The displayed hours are provisional, so calling ahead is sensible."],
  ["Do you deliver locally?", "Yes. Home delivery and in-store pickup are available. Ask us about timing and cost for your postcode."],
  ["Can you help if another floor was fitted badly?", "Yes. We will inspect the floor, explain what has gone wrong in plain English and tell you honestly what can be put right."],
];

export const posts = [
  {
    slug: "lvt-vs-laminate-lincolnshire",
    title: "LVT or laminate: which suits your Lincolnshire home?",
    excerpt: "Both can look like timber. The real choice comes down to water, preparation, budget and how the room is used.",
    date: "2026-07-18",
    body: ["LVT and laminate can both create a convincing timber look, but they behave differently. LVT is water-resistant and especially useful in kitchens, hallways and utilities. Laminate often gives you excellent value in drier living spaces.", "The condition of the subfloor matters as much as the product. We inspect levels, moisture and transitions during your free measure, then explain the sensible options."],
  },
  {
    slug: "prepare-new-build-for-flooring",
    title: "Preparing a new-build home for flooring",
    excerpt: "A little planning before move-in makes measuring, preparation and fitting much smoother.",
    date: "2026-06-06",
    body: ["New-build floors can still need moisture checks and smoothing before the final finish goes down. Arrange your measure early, keep completion dates flexible and avoid booking furniture deliveries for fitting day.", "We can plan the sequence room by room and help you decide where a single floor should flow through and where a transition is more practical."],
  },
  {
    slug: "care-for-herringbone-lvt",
    title: "How to care for herringbone LVT",
    excerpt: "Simple habits keep the pattern crisp and the finish looking its best.",
    date: "2026-05-12",
    body: ["Sweep grit regularly, use a damp—not soaking—mop and choose a cleaner recommended for your LVT range. Felt pads beneath furniture help prevent scratching.", "The pattern attracts the eye, so small changes in sheen show quickly. Avoid steam cleaners unless the manufacturer specifically approves them."],
  },
];
